// src/components/farm/SimilarProducts.js
//
// "Similar products" feed rendered on the ProductDetails page.
//
// Reuses the existing FarmProductCard (same markup, same click-to-open behaviour,
// same responsive grid) so product cards are consistent across the app. On
// desktop the section sits just below the listing images on the left of the
// details page; on mobile it flows beneath the product details.
//
// States handled: loading (skeleton), populated, empty, error (with retry),
// offline (graceful degradation). All user-visible strings are i18n keys.

import React from 'react';
import { useTranslation } from 'react-i18next';
import FarmProductCard from './FarmProductCard';
import useFarmWishlist from '../../hooks/useFarmWishlist';
import useProductRecommendations from '../../hooks/useProductRecommendations';

const GRID_COLS = 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3 lg:gap-4';

function SkeletonCard() {
  return (
    <article
      aria-hidden="true"
      className="bg-white dark:bg-neutral-900 rounded-lg overflow-hidden border border-gray-100 dark:border-neutral-800 flex flex-col animate-pulse"
    >
      <div className="aspect-square bg-gray-200 dark:bg-neutral-700" />
      <div className="px-2 pt-1.5 pb-2 flex flex-col gap-0.5">
        <div className="h-3.5 w-2/3 bg-gray-200 dark:bg-neutral-700 rounded" />
        <div className="h-3 w-4/5 bg-gray-200 dark:bg-neutral-700 rounded" />
        <div className="h-2.5 w-1/2 bg-gray-200 dark:bg-neutral-700 rounded mt-auto" />
      </div>
    </article>
  );
}

export default function SimilarProducts({ product, currentUser = null, limit = 8 }) {
  const { t } = useTranslation(['farmMarketplace', 'common']);

  if (!product) return null;
  const { isWished, toggleWishlist, wishlistLoading } = useFarmWishlist();
  const { recommendations, loading, error, retry } = useProductRecommendations(
    product,
    currentUser,
    { limit }
  );

  const handleToggleWishlist = async (item) => {
    try {
      await toggleWishlist(item);
    } catch (err) {
      // Wishlist toggle surfaces its own toast / login redirect upstream.
    }
  };

  const renderGrid = (items) => (
    <section className={GRID_COLS} aria-label={t('farmMarketplace:similarProductsSection', 'Similar products')}>
      {items.map((item) => (
        <FarmProductCard
          key={item.id}
          product={item}
          compact
          isWished={isWished(item.id)}
          onToggleWishlist={handleToggleWishlist}
        />
      ))}
    </section>
  );

  return (
    <section className="mt-8" aria-live="polite">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
        {t('farmMarketplace:similarProducts', 'Similar products')}
      </h2>

      {loading && (
        <section className={GRID_COLS} aria-busy="true">
          {Array.from({ length: limit }).map((_, i) => (
            <SkeletonCard key={`skel-${i}`} />
          ))}
        </section>
      )}

      {!loading && error && (
        <div className="text-center py-10 text-sm text-gray-600 dark:text-gray-400">
          <p className="mb-3">
            {t('farmMarketplace:recommendationsError', 'We could not load similar products right now.')}
          </p>
          <button
            type="button"
            onClick={retry}
            className="inline-flex items-center justify-center px-4 py-2 bg-green-700 hover:bg-green-800 text-white text-sm font-medium rounded-full focus:outline-none focus:ring-2 focus:ring-green-600"
          >
            {t('common:retry', 'Retry')}
          </button>
        </div>
      )}

      {!loading && !error && recommendations.length === 0 && (
        <div className="text-center py-10 text-sm text-gray-500 dark:text-gray-400">
          <p>{t('farmMarketplace:noRecommendations', 'No similar products found right now.')}</p>
        </div>
      )}

      {!loading && !error && recommendations.length > 0 && renderGrid(recommendations)}
    </section>
  );
}
